import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useAppDispatch from "../../hooks/useAppDispatch";
import {
    fetchNotifications,
    fetchUnreadCount,
    markNotificationRead,
    markAllRead,
    removeNotification,
} from "../../store/notificationSlice";
import type { RootState } from "../../store/store";
import NotificationItem from "../../components/navigation-bars/NotificationItem";
import { Check, Filter, Loader2, RefreshCw } from "lucide-react";
import { FaBell } from "react-icons/fa";
import { Button } from "../../components/misc/Button";
import { useLocation, useNavigate } from "react-router-dom";

interface LocationState {
    notificationId: string;
}

const NotificationsPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const locState = location.state as LocationState;

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const { list, unreadCount, loading } = useSelector(
        (state: RootState) => state.notifications
    );
    const [filter, setFilter] = useState<"all" | "unread">("all");
    const [page, setPage] = useState(1);
    const limit = 10;

    useEffect(() => {
        if (locState?.notificationId) {
            setExpandedId(locState.notificationId);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [locState, navigate, location.pathname]);

    useEffect(() => {
        dispatch(fetchNotifications({ page, limit }));
        // Re-sync the badge count from the backend on every page/load change
        // so prior overlay mark-reads and page-level mark-reads stay accurate.
        dispatch(fetchUnreadCount());
    }, [dispatch, page]);

    const handleMarkAllRead = () => {
        dispatch(markAllRead());
    };

    const handleLoadMore = () => {
        setPage((prev) => prev + 1);
    };

    const handleRefresh = () => {
        setPage(1);
        dispatch(fetchNotifications({ page: 1, limit }));
    };

    const filteredNotifications =
        filter === "unread" ? list.filter((n) => !n.isRead) : list;

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 overflow-x-hidden">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-primary mb-2">
                    Notifications
                </h1>
                <p className="text-gray-600">
                    Stay updated with the latest activities and alerts
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-y-2 p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <FaBell className="w-5 h-5 text-secondary" />
                        <span className="font-medium text-gray-700">
                            {unreadCount > 0
                                ? `${unreadCount} unread`
                                : "All caught up"}
                        </span>
                    </div>

                    <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                        <div className="relative">
                            <button
                                className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-md transition-colors"
                                onClick={() =>
                                    setFilter((prev) =>
                                        prev === "all" ? "unread" : "all"
                                    )
                                }
                            >
                                <Filter className="w-4 h-4 mr-1.5" />
                                {filter === "all" ? "All" : "Unread"}
                            </button>
                            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-10 hidden">
                                <button
                                    onClick={() => setFilter("all")}
                                    className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setFilter("unread")}
                                    className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                                >
                                    Unread
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={handleRefresh}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                            title="Refresh notifications"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>

                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors"
                            >
                                <Check className="w-4 h-4 mr-1" />
                                Mark all read
                            </button>
                        )}
                    </div>
                </div>

                {loading && page === 1 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="w-10 h-10 text-secondary animate-spin mb-3" />
                        <p className="text-gray-500">
                            Loading notifications...
                        </p>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                        <div className="bg-gray-100 p-4 rounded-full mb-4">
                            <FaBell className="w-8 h-8 text-secondary" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-800 mb-1">
                            No notifications
                        </h3>
                        <p className="text-gray-500 max-w-sm">
                            {filter === "unread"
                                ? "You've read all your notifications. Switch to 'All' to see your notification history."
                                : "When you receive notifications, they will appear here."}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {filteredNotifications.map((notification) => (
                            <NotificationItem
                                key={notification._id}
                                notification={notification}
                                onRead={() =>
                                    dispatch(
                                        markNotificationRead(notification._id)
                                    )
                                }
                                onRemove={() =>
                                    dispatch(
                                        removeNotification(notification._id)
                                    )
                                }
                                isExpanded={notification._id === expandedId}
                                onToggle={() =>
                                    setExpandedId((prev) =>
                                        prev === notification._id
                                            ? null
                                            : notification._id
                                    )
                                }
                            />
                        ))}

                        {loading && page > 1 && (
                            <div className="flex justify-center py-6">
                                <Loader2 className="w-6 h-6 text-secondary animate-spin" />
                            </div>
                        )}

                        {!loading && list.length >= page * limit && (
                            <div className="p-4 text-center">
                                <Button onClick={handleLoadMore}>
                                    Load more
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;
